import { FormEvent, useEffect, useMemo, useState } from "react";
import completeCharacter from "./static/img/figma-inquiry-complete-character.png";
import "../../components/MobileAppShell/MobileAppShell.css";
import "./MobileInquiryPage.css";

type MobileInquiryView = "list" | "write" | "complete" | "detail";
type MobileInquiryFilter = "all" | "pending" | "done";

export type MobileInquiryPost = {
    post_pk: number | string;
    post_title?: string;
    post_contents: string;
    post_insert_dtm: string;
    answer_status?: string;
    answer_contents?: string | null;
    answer_insert_dtm?: string | null;
    category?: string;
    unread_reply_count?: number;
    messages?: Array<{
        id: string;
        sender_type: "CUSTOMER" | "STAFF" | "SYSTEM";
        body: string;
        created_at: string;
    }>;
};

type SubmitPayload = {
    category: string;
    title: string;
    content: string;
    files: File[];
};

type MobileInquiryPageProps = {
    initialView: MobileInquiryView;
    selectedPost?: MobileInquiryPost | null;
    posts: MobileInquiryPost[];
    isLoading: boolean;
    errorMessage?: string;
    unavailableReason?: string;
    onRetry?: () => void;
    onBack: () => void;
    onOpenWrite: () => void;
    onOpenList: () => void;
    onOpenDetail: (post: MobileInquiryPost) => void;
    onSubmit: (payload: SubmitPayload) => Promise<void>;
    onReply: (message: string) => Promise<void>;
};

const CATEGORIES = ["상품 제작", "촬영 일정", "사진 선택", "배송 정보", "기타 문의"];
const MAX_CONTENT_LENGTH = 50;

function MobileInquiryStatusBar() {
    return (
        <div className="mobile-inquiry-status" aria-hidden="true">
            <span>10:20</span>
            <span className="mobile-inquiry-status__icons"><i /><i /><i /></span>
        </div>
    );
}

function BackIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="m15 5-7 7 7 7" />
        </svg>
    );
}

function ChevronIcon() {
    return (
        <svg viewBox="0 0 16 10" aria-hidden="true" focusable="false">
            <path d="m2 2 6 6 6-6" />
        </svg>
    );
}

function CheckCircleIcon() {
    return (
        <svg viewBox="0 0 56 56" aria-hidden="true" focusable="false">
            <circle cx="28" cy="28" r="23" />
            <path d="m18.5 28.2 6.3 6.3 13-14" />
        </svg>
    );
}

function MobileInquiryHeader({ title, onBack }: { title: string; onBack: () => void }) {
    return (
        <header className="mobile-inquiry-header" data-node-id="7138:8163">
            <button className="mobile-inquiry-header__back" type="button" aria-label="뒤로가기" onClick={onBack}>
                <BackIcon />
            </button>
            <h1>{title}</h1>
        </header>
    );
}

function RequiredLabel({ children }: { children: string }) {
    return <label className="mobile-inquiry-label">{children} <span>*</span></label>;
}

function formatInquiryDate(value?: string | null) {
    if(!value) return "";
    const normalized = value.replace(/-/g, ".");
    const matched = normalized.match(/\d{4}\.\d{2}\.\d{2}/);
    return matched?.[0] || normalized.slice(0, 10);
}

function formatInquiryTime(value?: string | null) {
    if(!value) return "";
    const matched = value.match(/\d{2}:\d{2}:\d{2}/);
    return matched?.[0] || "";
}

function isDoneStatus(status?: string) {
    return Boolean(status && status.replace(/\s/g, "").includes("답변완료"));
}

function getPostTitle(post?: MobileInquiryPost | null) {
    return post?.post_title || "제목 없음";
}

function getPostContents(post?: MobileInquiryPost | null) {
    return post?.post_contents || "문의 내용이 없습니다.";
}

function InquiryBadge({ status }: { status?: string }) {
    const done = isDoneStatus(status);
    return <span className={done ? "mobile-inquiry-status-badge mobile-inquiry-status-badge--done" : "mobile-inquiry-status-badge"}>{done ? "답변 완료" : "답변 대기"}</span>;
}

function MobileInquiryWrite({
    onBack,
    onComplete,
    onSubmit,
}: {
    onBack: () => void;
    onComplete: () => void;
    onSubmit: (payload: SubmitPayload) => Promise<void>;
}) {
    const [category, setCategory] = useState("");
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const files: File[] = [];
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if(!category || !title.trim() || !content.trim()){
            window.alert("필수 항목을 입력해주세요.");
            return;
        }

        setIsSubmitting(true);
        setSubmitError("");
        try{
            await onSubmit({ category, title: title.trim(), content: content.trim(), files });
            onComplete();
        }catch(error){
            setSubmitError(error instanceof Error ? error.message : "문의를 등록하지 못했습니다. 다시 시도해 주세요.");
        }finally{
            setIsSubmitting(false);
        }
    };

    return (
        <section className="mobile-inquiry-page mobile-app-frame" data-node-id="7138:7912">
            <MobileInquiryStatusBar />
            <MobileInquiryHeader title="문의하기" onBack={onBack} />
            <form className="mobile-inquiry-form" onSubmit={handleSubmit}>
                <section className="mobile-inquiry-fieldset mobile-inquiry-fieldset--category">
                    <div className="mobile-inquiry-title-row">
                        <strong>문의 내용이 뭔가요?</strong><span>*</span>
                    </div>
                    <label className="mobile-inquiry-select-wrap">
                        <select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="문의 카테고리 선택">
                            <option value="">카테고리를 선택해주세요</option>
                            {CATEGORIES.map((item) => <option key={item} value={item}>{item}</option>)}
                        </select>
                        <ChevronIcon />
                    </label>
                </section>

                <section className="mobile-inquiry-fieldset">
                    <RequiredLabel>제목</RequiredLabel>
                    <input
                        className="mobile-inquiry-input"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder="제목을 적어주세요."
                        autoComplete="off"
                    />
                </section>

                <section className="mobile-inquiry-fieldset mobile-inquiry-fieldset--content">
                    <RequiredLabel>내용</RequiredLabel>
                    <textarea
                        className="mobile-inquiry-textarea"
                        value={content}
                        maxLength={MAX_CONTENT_LENGTH}
                        onChange={(event) => setContent(event.target.value)}
                        placeholder="궁금한 점을 남겨주세요."
                    />
                    <span className="mobile-inquiry-count">({content.length}/{MAX_CONTENT_LENGTH}자)</span>
                </section>

                <section className="mobile-inquiry-fieldset mobile-inquiry-fieldset--photo" aria-disabled="true">
                    <h2>사진 첨부(선택)</h2>
                    <p>첨부 파일 기능은 준비 중입니다. 문의 내용은 지금 바로 등록할 수 있습니다.</p>
                </section>

                <div className="mobile-inquiry-submit-bar" data-node-id="7138:7919">
                    {submitError ? <p className="mobile-inquiry-submit-error" role="alert">{submitError}</p> : null}
                    <button className="mobile-inquiry-submit" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "등록 중" : "등록하기"}
                    </button>
                </div>
            </form>
        </section>
    );
}

function MobileInquiryComplete({ onConfirm }: { onConfirm: () => void }) {
    return (
        <section className="mobile-inquiry-page mobile-app-frame mobile-inquiry-page--complete" data-node-id="7138:8114">
            <MobileInquiryStatusBar />
            <MobileInquiryHeader title="문의하기" onBack={onConfirm} />
            <main className="mobile-inquiry-complete-main">
                <div className="mobile-inquiry-complete-mark"><CheckCircleIcon /></div>
                <h2>문의 등록 완료!</h2>
                <img className="mobile-inquiry-complete-character" src={completeCharacter} alt="" />
                <p>문의 등록이 완료되었어요.<br />1:1 문의 내역은 마이페이지에서 확인 할 수 있어요</p>
                <button className="mobile-inquiry-complete-link" type="button" onClick={onConfirm}>내 문의글 확인하기 <span>›</span></button>
            </main>
            <div className="mobile-inquiry-submit-bar mobile-inquiry-submit-bar--complete" data-node-id="7138:8115">
                <button className="mobile-inquiry-submit" type="button" onClick={onConfirm}>확인</button>
            </div>
        </section>
    );
}

function MobileInquiryList({
    posts,
    isLoading,
    errorMessage,
    unavailableReason,
    onRetry,
    onBack,
    onOpenWrite,
    onOpenDetail,
}: {
    posts: MobileInquiryPost[];
    isLoading: boolean;
    errorMessage?: string;
    unavailableReason?: string;
    onRetry?: () => void;
    onBack: () => void;
    onOpenWrite: () => void;
    onOpenDetail: (post: MobileInquiryPost) => void;
}) {
    const [filter, setFilter] = useState<MobileInquiryFilter>("all");
    const sourcePosts = posts;
    const visiblePosts = useMemo(() => {
        if(filter === "pending") return sourcePosts.filter((post) => !isDoneStatus(post.answer_status));
        if(filter === "done") return sourcePosts.filter((post) => isDoneStatus(post.answer_status));
        return sourcePosts;
    }, [filter, sourcePosts]);

    return (
        <section className="mobile-inquiry-page mobile-app-frame mobile-inquiry-page--list" data-node-id="7138:8162">
            <MobileInquiryStatusBar />
            <MobileInquiryHeader title="1:1 문의" onBack={onBack} />
            <nav className="mobile-inquiry-tabs" aria-label="문의 상태 필터" data-node-id="7138:8276">
                <button className={filter === "all" ? "is-active" : ""} type="button" onClick={() => setFilter("all")}>전체</button>
                <button className={filter === "pending" ? "is-active" : ""} type="button" onClick={() => setFilter("pending")}>답변 대기</button>
                <button className={filter === "done" ? "is-active" : ""} type="button" onClick={() => setFilter("done")}>답변 완료</button>
            </nav>
            <main className="mobile-inquiry-list-main">
                {errorMessage ? (
                    <div className="mobile-inquiry-empty" role="alert">
                        <p>{errorMessage}</p>
                        {onRetry ? <button type="button" onClick={onRetry}>다시 시도</button> : null}
                    </div>
                ) : null}
                {!errorMessage && unavailableReason ? (
                    <p className="mobile-inquiry-empty" role="status">{unavailableReason}</p>
                ) : null}
                {!errorMessage && !unavailableReason && isLoading && posts.length === 0 ? <p className="mobile-inquiry-empty" role="status">문의 내역을 불러오는 중입니다.</p> : null}
                {!errorMessage && !unavailableReason && !isLoading && visiblePosts.length === 0 ? <p className="mobile-inquiry-empty">해당 문의가 없습니다.</p> : null}
                <div className="mobile-inquiry-card-list" data-node-id="7138:8342">
                    {visiblePosts.map((post) => (
                        <button key={post.post_pk} className="mobile-inquiry-card" type="button" onClick={() => onOpenDetail(post)}>
                            <div className="mobile-inquiry-card__meta">
                                <InquiryBadge status={post.answer_status} />
                                <time>{formatInquiryDate(post.post_insert_dtm)}</time>
                            </div>
                            <strong>{getPostTitle(post)}</strong>
                            <p>{getPostContents(post)}</p>
                        </button>
                    ))}
                </div>
            </main>
            <div className="mobile-inquiry-submit-bar mobile-inquiry-submit-bar--list" data-node-id="7146:7814">
                <button className="mobile-inquiry-submit" type="button" onClick={onOpenWrite}>문의하기</button>
            </div>
        </section>
    );
}

function MobileInquiryDetail({
    post,
    onBack,
    onReply,
}: {
    post: MobileInquiryPost;
    onBack: () => void;
    onReply: (message: string) => Promise<void>;
}) {
    const done = isDoneStatus(post.answer_status);
    const answer = post.answer_contents || "";
    const [reply, setReply] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const messages = post.messages || [];

    const submitReply = async () => {
        const value = reply.trim();
        if (!value || isSubmitting) return;
        setIsSubmitting(true);
        setSubmitError("");
        try {
            await onReply(value);
            setReply("");
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : "추가 문의를 등록하지 못했습니다.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="mobile-inquiry-page mobile-app-frame mobile-inquiry-page--detail" data-node-id={done ? "7146:1065" : "7138:8214"}>
            <MobileInquiryStatusBar />
            <MobileInquiryHeader title="문의하기" onBack={onBack} />
            <main className="mobile-inquiry-detail-main">
                <section className="mobile-inquiry-detail-head">
                    <InquiryBadge status={post.answer_status} />
                    <h2>{getPostTitle(post)}</h2>
                    <p>{formatInquiryDate(post.post_insert_dtm)} <span>{formatInquiryTime(post.post_insert_dtm)}</span></p>
                </section>

                <section className="mobile-inquiry-detail-section">
                    <h3>문의 내용</h3>
                    <p>{getPostContents(post)}</p>
                </section>

                {messages.length > 0 ? (
                    <section className="mobile-inquiry-thread" aria-label="문의 대화">
                        <h3>스튜디오와 대화</h3>
                        {messages.map((message) => (
                            <article
                                key={message.id}
                                className={`mobile-inquiry-thread__message mobile-inquiry-thread__message--${message.sender_type.toLowerCase()}`}
                            >
                                <strong>{message.sender_type === "CUSTOMER" ? "고객" : "스튜디오"}</strong>
                                <p>{message.body}</p>
                                <time>{formatInquiryDate(message.created_at)} {formatInquiryTime(message.created_at)}</time>
                            </article>
                        ))}
                    </section>
                ) : done && answer ? (
                    <section className="mobile-inquiry-answer-card">
                        <h3>스튜디오 답변</h3>
                        <p>{answer}</p>
                        <time>{formatInquiryDate(post.answer_insert_dtm)} {formatInquiryTime(post.answer_insert_dtm)}</time>
                    </section>
                ) : null}

                <section className="mobile-inquiry-reply">
                    <label htmlFor="inquiry-reply">추가 문의</label>
                    <textarea
                        id="inquiry-reply"
                        value={reply}
                        onChange={(event) => setReply(event.target.value)}
                        maxLength={5000}
                        placeholder="이 문의에 이어서 궁금한 내용을 남겨주세요."
                    />
                    {submitError ? <p className="mobile-inquiry-reply__error">{submitError}</p> : null}
                    <button type="button" disabled={!reply.trim() || isSubmitting} onClick={submitReply}>
                        {isSubmitting ? "등록 중..." : "추가 문의 등록"}
                    </button>
                </section>
            </main>
        </section>
    );
}

export default function MobileInquiryPage({
    initialView,
    selectedPost,
    posts,
    isLoading,
    errorMessage,
    unavailableReason,
    onRetry,
    onBack,
    onOpenWrite,
    onOpenList,
    onOpenDetail,
    onSubmit,
    onReply,
}: MobileInquiryPageProps) {
    const sourcePosts = posts;
    const [view, setView] = useState<MobileInquiryView>(initialView);
    const [activePost, setActivePost] = useState<MobileInquiryPost | null>(selectedPost || sourcePosts[0] || null);

    useEffect(() => {
        setView(initialView);
    }, [initialView]);

    useEffect(() => {
        if(selectedPost){
            setActivePost(selectedPost);
            return;
        }

        if(initialView === "detail"){
            setActivePost(sourcePosts[0] || null);
        }
    }, [initialView, selectedPost, sourcePosts]);

    const openList = () => {
        setView("list");
        onOpenList();
    };

    const openWrite = () => {
        setView("write");
        onOpenWrite();
    };

    if(view === "write"){
        return (
            <MobileInquiryWrite
                onBack={openList}
                onSubmit={onSubmit}
                onComplete={() => setView("complete")}
            />
        );
    }

    if(view === "complete"){
        return <MobileInquiryComplete onConfirm={openList} />;
    }

    if(view === "detail" && activePost){
        return (
            <MobileInquiryDetail
                post={activePost}
                onBack={openList}
                onReply={onReply}
            />
        );
    }

    return (
        <MobileInquiryList
            posts={posts}
            isLoading={isLoading}
            errorMessage={errorMessage}
            unavailableReason={unavailableReason}
            onRetry={onRetry}
            onBack={onBack}
            onOpenWrite={openWrite}
            onOpenDetail={(post) => {
                setActivePost(post);
                setView("detail");
                onOpenDetail(post);
            }}
        />
    );
}
